import {
  Collection,
  type IBaseAttributes,
  isWretchError,
  type TConstraints,
  wretch,
} from '@feathr/rachis';

import Session from '../Session';
import { TargetableClass } from './targetable.types';
import { type ITargetableBase, TargetableBase } from './targetable_base';

export enum ETTDEntityState {
  erroring = 'erroring',
  healthy = 'healthy',
  unverified = 'unverified',
  verifying = 'verifying',
}
export interface ITargetInterestResponse extends IBaseAttributes {
  targetables: InterestTargetable[];
  categories: string[];
}

/**
 * Meta-specific settings for a target interest
 */
interface ITargetInterestMetaConfig {
  /** Whether Meta allows this interest on a campaign with a special ad category. */
  is_sac_safe: boolean;
}

/**
 * Interface for TradeDesk target interest
 */
interface ITradedeskTargetInterest {
  id: string;
  category_name: string;
  description: string;
  name: string;
  is_popular: boolean;
  state: ETTDEntityState;
  meta_config?: ITargetInterestMetaConfig;
}

/**
 * Interface for interest targetables
 */
export interface IInterestTargetable extends ITargetableBase {
  _cls: TargetableClass.interest;
  interest: ITradedeskTargetInterest;
}

/**
 * InterestTargetable class for interest targetables
 */
export class InterestTargetable extends TargetableBase<IInterestTargetable> {
  public override className = 'Targetable.TradedeskInterestTargetable';

  public override getDefaults(): Partial<IInterestTargetable> {
    return {
      ...super.getDefaults(),
      _cls: TargetableClass.interest,
    };
  }

  public override get constraints(): TConstraints<IInterestTargetable> {
    return {
      ...super.constraints,
      _cls: {
        inclusion: [TargetableClass.interest],
      },
    };
  }
}

export class InterestTargetables extends Collection<InterestTargetable> {
  public override getClassName(): string {
    return 'targetables';
  }

  public getModel(attributes: Partial<IInterestTargetable>): InterestTargetable {
    return new InterestTargetable(attributes);
  }

  // URL override methods
  public override url(): string {
    return `${super.url()}target_interests`;
  }

  /**
   * Fetches interest targetables and their categories from the API.
   *
   * @param channel - Optional channel identifier (e.g. `'tradedesk'`) to filter
   *   interests by eligibility. When omitted, all interests are returned.
   * @returns The filtered interest targetables and their category names.
   */
  public async getInterests(
    channel?: string,
  ): Promise<Pick<ITargetInterestResponse, 'targetables' | 'categories'>> {
    let url = this.url();
    if (channel) {
      const params = new URLSearchParams({ channels: channel });
      url += `?${params.toString()}`;
    }

    const response = await wretch<ITargetInterestResponse>(url, {
      headers: Session.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    const targetablesData = response.data?.targetables || [];

    // Clear existing models and create new ones
    this.reset();
    const targetables = targetablesData.map((data) => this.create(data));

    return {
      targetables,
      categories: response.data?.categories || [],
    };
  }
}
